type Child = Node | string | number | null | undefined | false;

export type Props<T extends HTMLElement> = Partial<Omit<T, 'style' | 'children' | 'dataset'>> & {
  class?: string;
  style?: Partial<CSSStyleDeclaration>;
  dataset?: Record<string, string>;
  attrs?: Record<string, string>;
  on?: { [K in keyof HTMLElementEventMap]?: (ev: HTMLElementEventMap[K]) => void };
};

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props?: Props<HTMLElementTagNameMap[K]> | null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props) {
    const { class: className, style, dataset, attrs, on, ...rest } = props;
    if (className) el.className = className;
    if (style) Object.assign(el.style, style);
    if (dataset) for (const [k, v] of Object.entries(dataset)) el.dataset[k] = v;
    if (attrs) for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    if (on) for (const [k, v] of Object.entries(on)) if (v) el.addEventListener(k, v as EventListener);
    Object.assign(el, rest);
  }
  append(el, children);
  return el;
}

export function append(parent: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    parent.appendChild(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
}

export function replaceChildren(parent: HTMLElement, ...children: Child[]): void {
  parent.replaceChildren();
  append(parent, children);
}

export function formatNumber(v: number, digits = 4): string {
  if (!Number.isFinite(v)) return '–';
  if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return v.toFixed(digits);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms.toFixed(0)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  return `${Math.floor(s / 60)}m ${(s % 60).toFixed(0)}s`;
}

/** Short random id for designs and DOM keys. */
export function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

/** "just now", "3 min ago", "2 h ago", then a short date. */
export function relTime(ts: number, now = Date.now()): string {
  const d = (now - ts) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)} min ago`;
  if (d < 86400) return `${Math.floor(d / 3600)} h ago`;
  return new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
